"use client";

import {
  Button,
  createTheme,
  FileInput,
  MantineProvider,
  Modal,
  MultiSelect,
  NumberInput,
  PasswordInput,
  ScrollArea,
  Select,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { ReactNode } from "react";

const MantineGlobalLayout = ({ children }: { children: ReactNode }) => {
  const theme = createTheme({
    defaultRadius: "sm",
    radius: {
      xs: "2px",
      sm: "4px",
      md: "6px",
      lg: "8px",
      xl: "12px",
      max: "16px",
    },
    colors: {
      primary: [
        "#EFF6FF",
        "#dbeafe",
        "#bfdbfe",
        "#93c5fd",
        "#60a5fa",
        "#3b82f6",
        "#2563EB", // Main Brand Color
        "#1d4ed8",
        "#1e40af",
        "#1e3a8a",
      ],
      slate: [
        "#F9FAFB",
        "#F3F4F6",
        "#E5E7EB",
        "#D1D5DC",
        "#99A1AF",
        "#6A7282",
        "#45556C",
        "#364153",
        "#1E2939",
        "#101828",
        "#030712",
      ],
    },
    primaryColor: "primary",
    primaryShade: 6,
    fontFamily: "Inter, sans-serif",
    components: {
      Text: Text.extend({
        defaultProps: {
          c: "slate.7",
        },
      }),
      Title: Title.extend({
        defaultProps: {
          c: "slate.8",
          fw: 600,
        },
      }),
      Modal: Modal.extend({
        defaultProps: {
          radius: "lg",
          centered: true,
          scrollAreaComponent: ScrollArea.Autosize,
          closeOnEscape: false,
          closeOnClickOutside: false,
          overlayProps: {
            backgroundOpacity: 0.6,
            blur: 3,
          },
        },
      }),
      TextInput: TextInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      PasswordInput: PasswordInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      Textarea: Textarea.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      FileInput: FileInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),

      NumberInput: NumberInput.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      Select: Select.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
        },
      }),
      MultiSelect: MultiSelect.extend({
        defaultProps: {
          radius: "md",
          labelProps: {
            c: "slate.6",
            fz: "14px",
            fw: 500,
          },
          descriptionProps: {
            c: "slate.5",
            fz: "12px",
          },
        },

        styles: () => ({
          // Pill container
          pill: {
            backgroundColor: "var(--mantine-color-primary-1)",
            color: "var(--mantine-color-primary-7)",
            fontWeight: 600,
          },
        }),
      }),

      Button: Button.extend({
        defaultProps: {
          fw: 400,
          fz: "15px",
          size: "md",
          radius: "md",
        },
      }),
      Badge: {
        defaultProps: {
          radius: "sm",
          fw: 600,
        },
      },
    },
  });

  return <MantineProvider theme={theme}>{children}</MantineProvider>;
};

export default MantineGlobalLayout;
